"use client";

import { useState } from "react";
import RedirectsCard from "./RedirectsCard";
import type { StoryblokRedirect, RedirectAnalysisResults } from "@/types/redirects";

const AnalyzeResultsModal = ({
  foundIssues,
  stagedRedirects,
  updateRedirect,
  closeModal,
  deleteRedirect,
  undoChanges,
  saveRedirect,
}: {
  foundIssues: RedirectAnalysisResults;
  stagedRedirects: StoryblokRedirect[];
  updateRedirect: (
    _uid: string,
    source: string,
    destination: string,
    statusCode: string
  ) => void;
  closeModal: () => void;
  undoChanges: (_uid: string) => void;
  deleteRedirect: (_uid: string) => void;
  saveRedirect: (
    _uid: string,
    source: string,
    destination: string,
    statusCode: string
  ) => void;
}) => {
  return (
    <div className="bg-green-500 rounded-lg shadow-md p-4 w-[800px] h-full font-sans z-10">
      <div className="flex gap-2 mb-4">
        <button
          className="px-3 py-1 bg-gray-300 text-black rounded hover:bg-gray-400"
          onClick={closeModal}
        >
          Close
        </button>
      </div>
      <p className="text-lg font-semibold mb-2">Duplicates</p>
      {Object.values(foundIssues.duplicates).map((duplicates, index) => (
        <ListOfRedirects
          key={`duplicate-${index}`}
          list={duplicates}
          updateRedirect={updateRedirect}
          deleteRedirect={deleteRedirect}
          undoChanges={undoChanges}
          saveRedirect={saveRedirect}
          stagedRedirects={stagedRedirects}
        />
      ))}
      <p className="text-lg font-semibold mt-6 mb-2">Chains</p>
      {Object.values(foundIssues.chains).map((chains, index) => (
        <ListOfRedirects
          key={`chain-${index}`}
          list={chains}
          updateRedirect={updateRedirect}
          deleteRedirect={deleteRedirect}
          undoChanges={undoChanges}
          saveRedirect={saveRedirect}
          stagedRedirects={stagedRedirects}
        />
      ))}
    </div>
  );
};

const ListOfRedirects = ({
  list,
  stagedRedirects,
  updateRedirect,
  deleteRedirect,
  undoChanges,
  saveRedirect,
}: {
  updateRedirect: (
    _uid: string,
    source: string,
    destination: string,
    statusCode: string
  ) => void;
  stagedRedirects: StoryblokRedirect[];
  list: StoryblokRedirect[];
  undoChanges: (_uid: string) => void;
  deleteRedirect: (_uid: string) => void;
  saveRedirect: (
    _uid: string,
    source: string,
    destination: string,
    statusCode: string
  ) => void;
}) => {
  const [color] = useState(getRandomColor());

  return list.map((duplicate: StoryblokRedirect) => {
    const stagedRedirect = stagedRedirects.find(
      (redirect: StoryblokRedirect) => redirect._uid === duplicate._uid
    );

    if (stagedRedirect) {
      const isModified =
        JSON.stringify(stagedRedirect) !== JSON.stringify(duplicate);

      return (
        <RedirectsCard
          redirect={stagedRedirect}
          updateRedirect={updateRedirect}
          deleteRedirect={deleteRedirect}
          undoChanges={undoChanges}
          saveRedirect={saveRedirect}
          key={duplicate._uid}
          isModified={isModified}
          backgroundColor={color}
        />
      );
    }

    return (
      <RedirectsCard
        redirect={duplicate}
        key={duplicate._uid}
        isDeleted
        undoChanges={undoChanges}
        backgroundColor={color}
      />
    );
  });
};

export default AnalyzeResultsModal;

function getRandomColor() {
  let r, g, b, brightness;

  do {
    r = Math.floor(Math.random() * 256);

    g = Math.floor(Math.random() * 256);

    b = Math.floor(Math.random() * 256);

    brightness = 0.299 * r + 0.587 * g + 0.114 * b;
  } while (brightness < 128);

  const toHex = (x: number) => x.toString(16).padStart(2, "0");

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
